import { useEffect, useRef, useState } from "react";
import { Play, Lock, Pause, Volume2, VolumeX, Maximize, Film, Timer } from "lucide-react";
import { Link } from "react-router-dom";

function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  if (!targetIso) return null;
  const target = new Date(targetIso).getTime();
  const diff = Math.max(0, target - now);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { h, m, s, expired: diff === 0 };
}

export default function MockPlayer({ film, locked, accessMode, expiresAt }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, []);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
    setHasStarted(true);
  };

  if (locked) {
    return (
      <div
        data-testid="locked-player"
        className="relative aspect-video w-full bg-gs-screenSurface border border-white/10 overflow-hidden flex items-center justify-center"
        style={{ backgroundImage: `linear-gradient(rgba(5,5,5,0.6),rgba(5,5,5,0.95)), url(${film.backdrop_url})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
      <div className="absolute inset-0 flex items-center justify-center bg-gs-ink/70" data-testid="locked-overlay-bg" />
      <div className="relative text-center max-w-md px-8 z-10">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gs-ember/15 border border-gs-ember/50 mb-6">
          <Lock className="text-gs-ember" size={28} />
        </div>
        <p className="font-mono text-[10px] uppercase tracking-[0.5em] text-gs-ember mb-4">Doors closed</p>
        <h3 className="font-display text-4xl text-gs-cream mb-3 tracking-[0.05em]">RENT OR BUY TO WATCH</h3>
        <p className="font-serif text-base text-gs-cream/70 leading-relaxed italic">
          Once you complete checkout, this player unlocks instantly and the film begins your private theatrical screening.
        </p>
      </div>
      </div>
    );
  }

  return (
    <div data-testid="mock-player" className="relative aspect-video w-full bg-black border border-white/10 overflow-hidden group">
      {countdown && (
        <div data-testid="rent-countdown" className="absolute top-4 right-4 z-10 bg-gs-ink/95 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-gs-ember inline-flex items-center gap-2 border border-gs-ember/50 shadow-[0_0_24px_rgba(201,169,97,0.25)]">
          <Timer size={11} /> Rental ends in {String(countdown.h).padStart(2, "0")}:{String(countdown.m).padStart(2, "0")}:{String(countdown.s).padStart(2, "0")}
        </div>
      )}
      <video
        ref={videoRef}
        src={film.video_url || "https://www.w3schools.com/html/mov_bbb.mp4"}
        className="w-full h-full object-cover"
        poster={film.backdrop_url}
        muted={muted}
        playsInline
      />
      {!hasStarted && (
        <button
          data-testid="play-overlay"
          onClick={toggle}
          className="absolute inset-0 flex items-center justify-center bg-gs-ink/60 hover:bg-gs-ink/40 transition-colors"
        >
          <span className="w-24 h-24 rounded-full bg-gs-ember/95 text-gs-ink flex items-center justify-center shadow-[0_0_70px_rgba(201,169,97,0.45)] hover:scale-105 transition-transform">
            <Play className="ml-1.5" size={36} fill="currentColor" />
          </span>
        </button>
      )}
      <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/90 to-transparent flex items-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={toggle} className="text-white hover:text-gs-neon">
          {playing ? <Pause size={18} /> : <Play size={18} />}
        </button>
        <button onClick={() => setMuted((m) => !m)} className="text-white hover:text-gs-neon">
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
        <span className="font-mono text-[10px] uppercase tracking-widest text-white/70 ml-2 inline-flex items-center gap-2">
          <Film size={12} /> Now playing · Green Screen Theater
        </span>
        <div className="flex-1" />
        {accessMode === "rent" && expiresAt && (
          <span className="font-mono text-[10px] uppercase tracking-widest text-gs-neon">
            Rental expires {new Date(expiresAt).toLocaleString()}
          </span>
        )}
        <button onClick={() => videoRef.current?.requestFullscreen?.()} className="text-white hover:text-gs-neon">
          <Maximize size={16} />
        </button>
      </div>
      <p className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">
        Mock screening · sample reel
      </p>
    </div>
  );
}

export function LinkToCheckout({ film, mode, children, className }) {
  return (
    <Link
      to={`/film/${film.slug}/checkout?mode=${mode}`}
      data-testid={`checkout-${mode}-link`}
      className={className}
    >
      {children}
    </Link>
  );
}
